/**
 * Offline Queue Service
 * 
 * Slaat wijzigingen lokaal op wanneer de server niet bereikbaar is.
 * Synchroniseert automatisch wanneer de verbinding terug is.
 */

const QUEUE_KEY = 'wasz_offline_queue';
const MAX_RETRIES = 5;
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Exponential backoff

class OfflineQueueService {
  constructor() {
    this.queue = this.loadQueue();
    this.isProcessing = false;
    this.listeners = new Set();
    this.isOnline = navigator.onLine;
    
    // Luister naar online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyListeners();
      this.processQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyListeners();
    });
    
    // Start queue processing bij initialisatie
    if (this.queue.length > 0 && this.isOnline) {
      setTimeout(() => this.processQueue(), 1000);
    }
  }
  
  // Laad queue uit localStorage
  loadQueue() {
    try {
      const stored = localStorage.getItem(QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error loading offline queue:', e);
      return [];
    }
  }
  
  // Sla queue op in localStorage
  saveQueue() {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
    } catch (e) {
      console.error('Error saving offline queue:', e);
    }
  }
  
  // Voeg actie toe aan queue
  addToQueue(action) {
    const queueItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      retries: 0,
      ...action
    };
    
    this.queue.push(queueItem);
    this.saveQueue();
    this.notifyListeners();
    
    // Probeer direct te verwerken als online
    if (this.isOnline && !this.isProcessing) {
      this.processQueue();
    }
    
    return queueItem.id;
  }
  
  // Verwijder item uit queue
  removeFromQueue(id) {
    this.queue = this.queue.filter(item => item.id !== id);
    this.saveQueue();
    this.notifyListeners();
  }
  
  // Verwerk de queue
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0 || !this.isOnline) {
      return;
    }
    
    this.isProcessing = true;
    this.notifyListeners();
    
    // Verwerk items in volgorde (FIFO)
    while (this.queue.length > 0 && this.isOnline) {
      const item = this.queue[0];
      
      try {
        await this.executeAction(item);
        // Succes - verwijder uit queue
        this.queue.shift();
        this.saveQueue();
        this.notifyListeners();
      } catch (error) {
        console.error('Queue item failed:', error);
        
        // Check of het een netwerk error is
        const isNetworkError = !navigator.onLine || 
          error.message === 'Network Error' ||
          error.code === 'ECONNABORTED';
        
        if (isNetworkError) {
          // Stop met verwerken - wacht op verbinding
          this.isOnline = false;
          break;
        }
        
        // Verhoog retry counter
        item.retries++;
        item.lastError = error.message;
        
        if (item.retries >= MAX_RETRIES) {
          // Te veel retries - markeer als gefaald
          item.failed = true;
          item.failedAt = new Date().toISOString();
          this.queue.shift();
          this.saveQueue();
          this.notifyListeners();
          
          // Emit failure event
          this.emitFailure(item);
        } else {
          // Wacht en probeer opnieuw
          const delay = RETRY_DELAYS[Math.min(item.retries - 1, RETRY_DELAYS.length - 1)];
          this.saveQueue();
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    this.isProcessing = false;
    this.notifyListeners();
  }
  
  // Voer een actie uit
  async executeAction(item) {
    const { type, endpoint, method, data, token } = item;
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const options = {
      method: method || 'POST',
      headers
    };
    
    if (data && method !== 'GET' && method !== 'DELETE') {
      options.body = JSON.stringify(data);
    }
    
    const response = await fetch(endpoint, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    
    return response.json().catch(() => ({}));
  }
  
  // Emit failure event voor UI feedback
  emitFailure(item) {
    const event = new CustomEvent('offlineQueueFailure', { detail: item });
    window.dispatchEvent(event);
  }
  
  // Subscribe voor status updates
  subscribe(listener) {
    this.listeners.add(listener);
    // Direct status doorgeven
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }
  
  // Notify alle listeners
  notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach(listener => listener(status));
  }
  
  // Haal huidige status op
  getStatus() {
    return {
      isOnline: this.isOnline,
      isProcessing: this.isProcessing,
      pendingCount: this.queue.filter(i => !i.failed).length,
      failedCount: this.queue.filter(i => i.failed).length,
      queue: [...this.queue]
    };
  }
  
  // Retry gefaalde items
  retryFailed() {
    this.queue.forEach(item => {
      if (item.failed) {
        item.failed = false;
        item.retries = 0;
        delete item.lastError;
        delete item.failedAt;
      }
    });
    this.saveQueue();
    this.notifyListeners();
    this.processQueue();
  }
  
  // Verwijder gefaalde items
  clearFailed() {
    this.queue = this.queue.filter(item => !item.failed);
    this.saveQueue();
    this.notifyListeners();
  }
  
  // Forceer sync (manual trigger)
  forceSync() {
    if (navigator.onLine) {
      this.isOnline = true;
      this.processQueue();
    }
  }
}

// Singleton instance
export const offlineQueue = new OfflineQueueService();

// Hook voor React components
export const useOfflineQueue = () => {
  const [status, setStatus] = React.useState(offlineQueue.getStatus());
  
  React.useEffect(() => {
    return offlineQueue.subscribe(setStatus);
  }, []);
  
  return {
    ...status,
    addToQueue: (action) => offlineQueue.addToQueue(action),
    retryFailed: () => offlineQueue.retryFailed(),
    clearFailed: () => offlineQueue.clearFailed(),
    forceSync: () => offlineQueue.forceSync()
  };
};

// Import React for the hook
import React from 'react';

export default offlineQueue;
